// ---------------------------------------------------------------------------
// Valley Tickets - Homepage marketing content (config-driven)
// Edit copy here; the homepage components render from these arrays.
// ---------------------------------------------------------------------------

// Real Valley Tickets contact info (RGV-built, local support).
export const SALES_EMAIL = 'valleyticketsrgv@gmail.com';
export const SALES_PHONE = '956-588-8168';
export const SALES_PHONE_HREF = '+19565888168';
export const WEBSITE = 'valley-tickets.com';

// Trust & Security points (honest practices, not certification claims).
export const TRUST = [
  { icon: '🔒', title: 'Secure payments', desc: 'Card payments are processed through Stripe. We never store raw card numbers.' },
  { icon: '🛡️', title: 'PCI-conscious checkout', desc: "Checkout is built to keep card data in Stripe's secure scope, off our servers." },
  { icon: '🎓', title: 'FERPA-aware data handling', desc: 'We minimize and protect student-related data and handle it with FERPA in mind.' },
  { icon: '🏫', title: 'Your data, your district', desc: 'Your district owns its event, ticket, and attendance data.' },
  { icon: '👥', title: 'Role-based access', desc: 'Staff see only what their role needs, from a single campus to the district office.' },
  { icon: '🧾', title: 'Audit-friendly records', desc: 'Clear, exportable reporting and records your office can reconcile and review.' },
];

// Pricing value props (no invented final pricing).
export const FEES = [
  { icon: '🏫', title: 'Schools keep 100%', desc: 'Your district keeps 100% of ticket revenue, every event.' },
  { icon: '👤', title: 'Attendee-paid convenience fee', desc: 'Convenience fees are paid by attendees, not the school.' },
  { icon: '💳', title: 'Attendee-paid processing', desc: 'Payment processing fees are paid by attendees, not the school.' },
  { icon: '🚫', title: 'No setup fees', desc: 'Nothing to pay to get your district started.' },
  { icon: '📄', title: 'No annual contracts', desc: 'No long-term lock-in. Stay because it works for your district.' },
  { icon: '🤝', title: 'Local support included', desc: 'Setup, launch, and event-day help from your RGV team, at no extra cost.' },
];

// SAMPLE tiered convenience-fee model (marketing examples only, not final pricing).
export const FEE_TIERS = [
  { range: '$0.00 – $5.00', fee: '$0.50' },
  { range: '$5.01 – $10.00', fee: '$0.75' },
  { range: '$10.01 – $20.00', fee: '$1.00' },
  { range: '$20.01 – $50.00', fee: '$1.50' },
  { range: '$50.01 – $100.00 / season pass', fee: '$2.00' },
  { range: '$100+ / packages', fee: 'Custom' },
];

// Example ticket breakdowns (sample only).
export const PRICE_EXAMPLES = [
  { label: '$5 Football Ticket', face: '$5.00', fee: '$0.50' },
  { label: '$20 Playoff Ticket', face: '$20.00', fee: '$1.00' },
  { label: '$50 Season Pass', face: '$50.00', fee: '$1.50' },
];

// Maps an average ticket price to its SAMPLE convenience fee (for the estimator).
export const sampleConvenienceFee = (price) => {
  if (price <= 5) return 0.5;
  if (price <= 10) return 0.75;
  if (price <= 20) return 1.0;
  if (price <= 50) return 1.5;
  if (price <= 100) return 2.0;
  return null; // custom
};

export const FEATURES = [
  { icon: '🎟️', title: 'Digital Ticketing', desc: 'Sell tickets online for any event in minutes. No cash boxes, no paper rolls, no will-call lines.' },
  { icon: '📲', title: 'Mobile Check-In', desc: 'Fans get an instant QR ticket on their phone. No app to download, ever.' },
  { icon: '📷', title: 'Ticket Scanning', desc: 'Scan at the gate from any phone or device for fast, accurate, fraud-proof entry.' },
  { icon: '🎭', title: 'Fine Arts Events', desc: 'Band, choir, orchestra, theatre and mariachi ticketed as easily as a playoff game.' },
  { icon: '🏈', title: 'Athletics', desc: 'Football, volleyball, basketball, soccer and tournaments with season and all-access passes.' },
  { icon: '📊', title: 'Reporting', desc: 'Real-time revenue and sales by school, event and category, exportable for the district office.' },
  { icon: '✅', title: 'Attendance Tracking', desc: 'Live gate headcounts, capacity limits and accurate attendance records for every event.' },
  { icon: '🏫', title: 'Multi-School Management', desc: 'Every campus and every program rolled up into one district command center.' },
];

export const STEPS = [
  { n: '01', title: 'Create Event', desc: 'Athletic and fine arts staff publish events and ticket types in minutes, no IT ticket required.' },
  { n: '02', title: 'Sell Tickets', desc: 'Parents buy in seconds on their phone and get an instant mobile QR ticket.' },
  { n: '03', title: 'Scan & Report', desc: 'Scan in at the gate; revenue and attendance roll up to one district dashboard in real time.' },
];

export const TESTIMONIALS = [
  {
    quote: 'Game-day lines disappeared. Parents pay on their phones and we scan them right in, and I can see every gate from my office.',
    name: 'Athletic Director',
    role: 'RGV School District',
    initials: 'AD',
  },
  {
    quote: 'Our band and theatre nights finally get the same easy ticketing as football. Setting up an event takes minutes.',
    name: 'Fine Arts Director',
    role: 'RGV School District',
    initials: 'FA',
  },
  {
    quote: "I bought tickets for two kids at two different schools in under a minute. No cash, no printing, it's just on my phone.",
    name: 'Parent',
    role: 'Rio Grande Valley',
    initials: 'P',
  },
];

export const STATS = [
  { value: '6+', label: 'Event types supported' },
  { value: 'Seconds', label: 'To buy a ticket' },
  { value: '1', label: 'Dashboard for the whole district' },
  { value: '100%', label: 'Mobile, no app required' },
];

export const FOOTER_LINKS = [
  {
    heading: 'Platform',
    links: [
      { label: 'Features', to: '/#features' },
      { label: 'District demos', to: '/demo' },
      { label: 'For districts', to: '/#for-districts' },
      { label: 'How it works', to: '/#how' },
    ],
  },
  {
    heading: 'For schools',
    links: [
      { label: 'Vendor login', to: '/vendor-login' },
      { label: 'Create an account', to: '/vendor-signup' },
      { label: 'See a demo', to: '/demo' },
    ],
  },
  {
    heading: 'Account',
    links: [
      { label: 'Customer login', to: '/login' },
      { label: 'My tickets', to: '/tickets' },
      { label: 'My account', to: '/account' },
    ],
  },
];
